import logProPurchasePageViewed from '@alltrails/analytics/events/logProPurchasePageViewed';
import NoSsr from '@material-ui/core/NoSsr';
import Typography from '@alltrails/shared/denali/components/Typography';
import ProFeatureComparisonTable from 'components/ProFeatureComparisonTable';
import useUser from 'hooks/useUser';
import { lazy, Suspense, useEffect, ReactNode } from 'react';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import Texture from '@alltrails/shared/denali/components/Texture';
import Coupon from 'types/Coupon';
import Plan from 'types/Plan';
import { getPurchaseScreenTypePlan } from 'utils/amplitudeHelper';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import chevronDownBouncing from 'components/shared/ChevronDownBouncing/chevron-down-bouncing.svg';
import { UserId } from '@alltrails/shared/types/User';
import PrimaryLink from 'components/LandingPages/PrimaryLink';
import usePlusFeatures from 'components/LandingPages/hooks/usePlusFeatures';
import * as styles from './PlusLandingPageBase.module.scss';
import heroDesktopJpg from './images/hero-2880w.jpg';
import heroDesktopWebp from './images/hero-2880w.webp';
import heroDesktopAvif from './images/hero-2880w.avif';
import heroMobileJpg from './images/hero-750w.jpg';
import heroMobileWebp from './images/hero-750w.webp';
import heroMobileAvif from './images/hero-750w.avif';

const ChevronDownBouncing = lazy(() => import('components/shared/ChevronDownBouncing'));

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=1591%3A72835
// https://www.figma.com/file/XvNAfKYeHFVgMflQBRwTpe/Plus-Purchase-Flow?type=design&node-id=3410%3A11139&t=6q0bwqEPi5Vxo84o-1

type Props = {
  buttonText: ReactNode;
  coupon?: Coupon;
  headline: ReactNode;
  subhead: ReactNode;
  title: ReactNode;
  userId?: UserId;
  plan: Plan;
};

export default function PlusLandingPageBase({ buttonText, coupon, headline, subhead, title, userId, plan }: Props) {
  useEffect(() => {
    logProPurchasePageViewed({
      purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
    });
  }, [plan, coupon]);

  const intl = useIntl();
  const user = useUser();

  const features = usePlusFeatures();

  const params = new URLSearchParams(__AT_DATA__.search);

  if (coupon) {
    params.set('k', coupon.code);
  }

  if (userId) {
    params.set('uid', String(userId));
  }

  const search = params.toString();

  const to = user || userId ? `/plus/checkout${search.length > 0 ? `?${search}` : ''}` : `/signup${search.length > 0 ? `?${search}` : ''}`;

  return (
    <div className={styles.wrapper}>
      <div className={styles.hero}>
        <picture className={styles.picture}>
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileAvif} type="image/avif" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileWebp} type="image/webp" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileJpg} type="image/jpg" />
          <source srcSet={heroDesktopAvif} type="image/avif" />
          <source srcSet={heroDesktopWebp} type="image/webp" />
          <img
            alt={intl.formatMessage({
              defaultMessage:
                'A trail runner hops joyfully along boulders surrounding a woodland stream. The sun-kissed water glistens, and the treetops glow vibrantly under a clear blue sky.'
            })}
            src={heroDesktopJpg}
          />
        </picture>
        <Texture variant="plus" className={styles.texture} />
      </div>
      <div className={styles.content}>
        <div className={styles.copy}>
          <div className={styles.headline}>{headline}</div>
          <div className={styles.title}>{title}</div>
          <div className={styles.subhead}>{subhead}</div>
          <div className={styles.button}>
            <PrimaryLink buttonText={buttonText} plan={plan} coupon={coupon} to={to} desktopVariant="accent" mobileVariant="primary" />
            <Typography className={styles.cancel} component="div" mb="8" variant="text100">
              <FormattedMessage defaultMessage="Cancel anytime." />
            </Typography>
          </div>
          <Typography className={styles.lottie} component="div" mb="8">
            <NoSsr>
              <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
                <ChevronDownBouncing />
              </Suspense>
            </NoSsr>
          </Typography>
        </div>
        <ProFeatureComparisonTable features={features} />
      </div>
    </div>
  );
}
